import React, { useState, useEffect } from 'react'
import { useNavigate } from 'react-router-dom'
import { getAllBusinesses } from '../services/business'
import './Index.css'

function Index() {
  const [businesses, setBusinesses] = useState([])
  const [filteredBusinesses, setFilteredBusinesses] = useState([])
  const [categories, setCategories] = useState([])
  const [selectedCategory, setSelectedCategory] = useState('all')
  const [searchQuery, setSearchQuery] = useState('')
  const [loading, setLoading] = useState(true)
  const navigate = useNavigate()

  useEffect(() => {
    fetchBusinesses()
  }, [])

  const fetchBusinesses = async () => {
    try {
      setLoading(true)
      const data = await getAllBusinesses()
      setBusinesses(data)
      setFilteredBusinesses(data)
    } catch (error) {
      console.error('Error fetching businesses:', error)
    } finally {
      setLoading(false)
    }
  }

  useEffect(() => {
    let results = businesses.filter(business => {
      const searchLower = searchQuery.toLowerCase()
      const categoryName = business.business_category?.name 
      const matchesSearch = (
        business.name?.toLowerCase().includes(searchLower) ||
        business.address?.toLowerCase().includes(searchLower) ||
        business.description?.toLowerCase().includes(searchLower) ||
        categoryName?.toLowerCase().includes(searchLower)
      )
      
      // Case-insensitive category comparison
      const matchesCategory = selectedCategory === 'all' || 
        categoryName?.toLowerCase() === selectedCategory?.toLowerCase()
      
      return matchesSearch && matchesCategory
    })

    // Sort alphabetically by name
    results = results.sort((a, b) => 
      (a.name || '').localeCompare(b.name || '')
    )

    setFilteredBusinesses(results)
  }, [searchQuery, businesses, selectedCategory])

  const handleBusinessClick = (businessId) => {
    navigate(`/booking?businessId=${businessId}`)
  }

  return (
    <div className="index-container">
      <div className="index-header">
        <h1>Find Your Business</h1>
        <p>Search and book appointments with local businesses</p>
      </div>

      <div className="search-controls">
        <div className="search-bar-container">
          <svg className="search-icon" xmlns="http://www.w3.org/2000/svg" viewBox="0 0 24 24" fill="none" stroke="currentColor">
            <circle cx="11" cy="11" r="8"/>
            <path d="m21 21-4.35-4.35"/>
          </svg>
          <input
            type="text"
            className="search-bar"
            placeholder="Search businesses by name, location, or category..."
            value={searchQuery}
            onChange={(e) => setSearchQuery(e.target.value)}
          />
          {searchQuery && (
            <button 
              className="clear-search"
              onClick={() => setSearchQuery('')}
              aria-label="Clear search"
            >
              ×
            </button>
          )}
        </div>
      </div>

      {categories.length > 0 && (
        <div className="category-filters">
          <button
            className={`category-filter-btn ${selectedCategory === 'all' ? 'active' : ''}`}
            onClick={() => setSelectedCategory('all')}
          >
            All Categories
          </button>
          {categories.map((category) => (
            <button
              key={category.id || category.name}
              className={`category-filter-btn ${selectedCategory === category.name ? 'active' : ''}`}
              onClick={() => setSelectedCategory(category.name)}
            >
              {category.name}
            </button>
          ))}
        </div>
      )}

      {loading ? (
        <div className="loading-container">
          <div className="loading-spinner"></div>
          <p>Loading businesses...</p>
        </div>
      ) : filteredBusinesses.length === 0 ? (
        <div className="no-results">
          <svg className="no-results-icon" xmlns="http://www.w3.org/2000/svg" viewBox="0 0 24 24" fill="none" stroke="currentColor">
            <circle cx="11" cy="11" r="8"/>
            <path d="m21 21-4.35-4.35"/>
          </svg>
          <h2>No businesses found</h2>
          <p>Try adjusting your search terms</p>
        </div>
      ) : (
        <>
          <div className="results-count">
            {filteredBusinesses.length} {filteredBusinesses.length === 1 ? 'business' : 'businesses'} found
          </div>
          <div className="business-grid">
            {filteredBusinesses.map((business) => (
              <div 
                key={business.id} 
                className="business-card"
                onClick={() => handleBusinessClick(business.id)}
              >
                {business.imageUrl && (
                  <div className="business-image">
                    <img src={business.imageUrl} alt={business.name} />
                  </div>
                )}
                <div className="business-content">
                  <h3 className="business-name">{business.name}</h3>
                  {business.category && (
                    <span className="business-category">
                      {typeof business.category === 'object' ? business.category.name : business.category}
                    </span>
                  )}
                  {business.description && (
                    <p className="business-description">{business.description}</p>
                  )}
                  {business.address && (
                    <div className="business-address">
                      <svg className="location-icon" xmlns="http://www.w3.org/2000/svg" viewBox="0 0 24 24" fill="none" stroke="currentColor">
                        <path d="M21 10c0 7-9 13-9 13s-9-6-9-13a9 9 0 0 1 18 0z"/>
                        <circle cx="12" cy="10" r="3"/>
                      </svg>
                      <span>{business.address}</span>
                    </div>
                  )}
                  {business.rating && (
                    <div className="business-rating">
                      <span className="rating-stars">★</span>
                      <span className="rating-value">{business.rating.toFixed(1)}</span>
                      {business.reviewCount && (
                        <span className="review-count">({business.reviewCount})</span>
                      )}
                    </div>
                  )}
                </div>
                <button className="book-button">
                  Book Appointment
                </button>
              </div>
            ))}
          </div>
        </>
      )}
    </div>
  )
}

export default Index